import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings,
  Edit3,
  MapPin,
  Link as LinkIcon,
  X,
  LogOut,
  Lock,
  Loader2,
  BookOpen,
} from "lucide-react";
import api, { unwrap } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import PostCard from "@/components/PostCard";
import PostThumb from "@/components/PostThumb";
import UserAvatar from "@/components/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Profile — 1:1 web port of iOS `src/app/(app)/(tabs)/profile.tsx`.
 *
 * The signed-in user's own profile:
 *   • Cursive display name (Grand Hotel 30px) + @username + verified badge
 *   • Bio, location, website chip
 *   • 3-stat row (Posts / Followers / Following) — tap Followers/Following
 *     to open the user list at `/u/:username/followers` (etc.)
 *   • Edit profile button + Settings gear + Log out (custom confirm dialog)
 *   • Two content tabs:
 *       Posts   — 3-per-row square image grid (Standard-only posts)
 *       Opinion — full-width opinion cards (using PostCard)
 *
 * Uses the current AuthContext for `user`, `refresh`, and `logout`.
 */

const RAINBOW =
  "linear-gradient(90deg, #FFD600, #FF7A00, #FF0069, #D300C5, #7638FA)";
// Brand purple via CSS variable so it auto-switches for dark mode
// (`#7638FA` light → `#9B6BFF` dark).
const BRAND = "hsl(var(--brand))";
const VERIFIED = "#3D9CFF";

function StatButton({ label, value, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center px-3 active:opacity-70 transition-opacity"
      data-testid={testId}
    >
      <span
        className="text-foreground font-extrabold tabular-nums"
        style={{ fontSize: 19, lineHeight: "24px" }}
      >
        {typeof value === "number" ? value.toLocaleString() : value ?? "0"}
      </span>
      <span className="text-muted-foreground text-[12px] mt-0.5">{label}</span>
    </button>
  );
}

function LogoutDialog({ onCancel, onConfirm, loggingOut }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-8"
      onClick={loggingOut ? undefined : onCancel}
      data-testid="logout-dialog"
    >
      <div
        className="w-full max-w-[320px] rounded-[22px] bg-card p-6 flex flex-col items-center gap-3 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: "rgba(255, 59, 48, 0.12)" }}
        >
          <LogOut size={20} className="text-[#FF3B30]" />
        </div>
        <h3 className="text-foreground font-bold text-[17px]">Log out?</h3>
        <p className="text-muted-foreground text-[13.5px] text-center leading-5">
          You&apos;ll be signed out on this device.
        </p>
        <div className="w-full flex gap-2.5 mt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loggingOut}
            className="flex-1 h-11 rounded-full bg-secondary text-foreground font-bold text-[14px] active:opacity-80 transition-opacity"
            data-testid="logout-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loggingOut}
            className="flex-1 h-11 rounded-full text-white font-bold text-[14px] active:opacity-85 transition-opacity flex items-center justify-center"
            style={{ backgroundColor: "#FF3B30" }}
            data-testid="logout-confirm"
          >
            {loggingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log out"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * PostThumbCell — wraps the shared PostThumb in a 3-per-row square cell.
 * iOS spec 14/15: paddingHorizontal 12, gap 3, cell width 32.5%,
 * aspectRatio 1, borderRadius 8.  The image itself resolves lazily via
 * usePostImage (cached per post id).
 */
function PostThumbCell({ post, onClick, testId }) {
  return (
    <div
      className="relative overflow-hidden rounded-lg"
      style={{ width: "32.5%", aspectRatio: "1 / 1" }}
    >
      <PostThumb post={post} onClick={onClick} testId={testId} />
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("posts");

  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [opinionPosts, setOpinionPosts] = useState(null);
  const [opinionLoading, setOpinionLoading] = useState(false);

  const [counts, setCounts] = useState({});
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const username = user?.username;
  const uid = user?.id ?? profile?.user_id;

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(EP.meProfile);
      let p = unwrap(res.data);
      p = p?.profile || p?.user || p;
      setProfile(p);
    } catch {
      setProfile(user);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Hydrate follower/following counts from the follow lists when the
  // profile record's own counters are missing or stale.
  useEffect(() => {
    if (!profile || !username) return;
    const rawFollowers = profile.followers_count ?? profile.follower_count;
    const rawFollowing = profile.following_count;
    const hasFollowers = typeof rawFollowers === "number" && rawFollowers > 0;
    const hasFollowing = typeof rawFollowing === "number" && rawFollowing > 0;
    if (hasFollowers && hasFollowing) return;
    let cancelled = false;
    const countOf = (raw) => {
      const d = unwrap(raw);
      if (typeof d?.itemsTotal === "number") return d.itemsTotal;
      if (typeof d?.total === "number") return d.total;
      const list = Array.isArray(d) ? d : d?.items || d?.users || d?.data;
      return Array.isArray(list) ? list.length : undefined;
    };
    (async () => {
      const [fr, gr] = await Promise.allSettled([
        hasFollowers ? Promise.reject(null) : api.get(EP.profileFollowersByUsername(username, 1, 50)),
        hasFollowing ? Promise.reject(null) : api.get(EP.profileFollowingByUsername(username, 1, 50)),
      ]);
      if (cancelled) return;
      setCounts((c) => ({
        followers:
          fr.status === "fulfilled" ? countOf(fr.value.data) ?? c.followers : c.followers,
        following:
          gr.status === "fulfilled" ? countOf(gr.value.data) ?? c.following : c.following,
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, [profile, username]);

  const parsePosts = (raw) => {
    const d = unwrap(raw);
    return Array.isArray(d) ? d : d?.items || d?.posts || d?.results || d?.data || [];
  };

  const loadPosts = useCallback(
    async (silent = false) => {
      if (!silent) setPostsLoading(true);
      let list = [];
      if (uid != null) {
        try {
          const res = await api.get(EP.profileStandardPosts(uid, 1, 30));
          list = parsePosts(res.data);
        } catch {
          list = [];
        }
      }
      // Fallback: derive from `by_username_posts` filtered to standards.
      if (list.length === 0 && username) {
        try {
          const res = await api.get(EP.profilePostsByUsername(username, 1, 30));
          const all = parsePosts(res.data);
          list = all.filter(
            (p) => (p.Post_type || p.post_type || "Standard").toLowerCase() !== "opinion"
          );
        } catch {
          /* keep empty */
        }
      }
      setPosts(list);
      setPostsLoading(false);
    },
    [uid, username]
  );

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Lazy opinion tab
  useEffect(() => {
    if (tab !== "opinion" || opinionPosts !== null) return;
    setOpinionLoading(true);
    (async () => {
      let list = [];
      if (uid != null) {
        try {
          const res = await api.get(EP.profileOpinionPosts(uid, 1, 30));
          list = parsePosts(res.data);
        } catch {
          list = [];
        }
      }
      // Fallback: derive from `by_username_posts` filtered to opinions.
      if (list.length === 0 && username) {
        try {
          const res = await api.get(EP.profilePostsByUsername(username, 1, 30));
          const all = parsePosts(res.data);
          list = all.filter(
            (p) => (p.Post_type || p.post_type || "").toLowerCase() === "opinion"
          );
        } catch {
          /* empty */
        }
      }
      setOpinionPosts(list);
      setOpinionLoading(false);
    })();
  }, [tab, uid, username, opinionPosts]);

  const doLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      toast.success("Logged out");
      setLogoutOpen(false);
      navigate("/", { replace: true });
    } catch {
      toast.error("Couldn't log out. Try again.");
    } finally {
      setLoggingOut(false);
    }
  };

  const openList = (kind) => navigate(`/${username}/${kind}`);

  if (loading || !profile) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center" data-testid="profile-loading">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: BRAND }} />
      </div>
    );
  }

  const displayName = profile.display_name || profile.username || "You";
  const bio = profile.bio;
  const location = profile.location;
  const website = profile.website;
  const followersVal =
    profile.followers_count ?? profile.follower_count ?? counts.followers ?? 0;
  const followingVal = profile.following_count ?? counts.following ?? 0;
  const postsVal = profile.posts_count ?? posts.length;

  return (
    <div className="w-full" data-testid="profile-screen">
      <div className="w-full md:max-w-none max-w-[560px] mx-auto md:mx-0">
        {/* Small in-screen top row for Settings gear (iOS spec 14
            renders "Profile" cursive title + gear here; the shared
            AppShell topbar already carries the brand + notifications
            + theme, so we only need the gear pinned on the right). */}
        <div className="flex items-center justify-end px-4 pt-1">
          <button
            type="button"
            onClick={() => navigate("/settings")}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
            aria-label="Settings"
            data-testid="profile-settings"
          >
            <Settings size={22} strokeWidth={1.8} className="text-foreground" />
          </button>
        </div>

        {/* Header — iOS spec 14: avatar LEFT (82px) + 3 stats RIGHT
            in a horizontal row.  Cursive display name + @username +
            bio + location/website below. */}
        <div className="px-4" data-testid="profile-header">
          <div className="flex items-center gap-4">
            <div
              className="rounded-full p-[3px] shrink-0"
              style={{ background: RAINBOW }}
            >
              <div className="rounded-full bg-background p-[3px]">
                <UserAvatar user={profile} size="xl" />
              </div>
            </div>
            <div className="flex-1 flex justify-around">
              <StatButton label="Posts" value={postsVal} onClick={() => setTab("posts")} testId="profile-stat-posts" />
              <StatButton
                label="Followers"
                value={followersVal}
                onClick={() => openList("followers")}
                testId="profile-stat-followers"
              />
              <StatButton
                label="Following"
                value={followingVal}
                onClick={() => openList("following")}
                testId="profile-stat-following"
              />
            </div>
          </div>

          <div className="mt-3.5 flex items-center gap-1.5 flex-wrap">
            <h1
              className="font-grand-hotel text-foreground"
              style={{ fontSize: 26, lineHeight: 1.15 }}
              data-testid="profile-display-name"
            >
              {displayName}
            </h1>
            {profile.is_verified && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill={VERIFIED}>
                <path
                  fill={VERIFIED}
                  d="M12 2c1.9 1.5 4.4 2.3 7 2v5c0 5.55-3.84 10.74-9 12-5.16-1.26-9-6.45-9-12V4c2.6.3 5.1-.5 7-2h4z"
                />
                <path
                  fill="#fff"
                  d="M10.5 15l-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4z"
                />
              </svg>
            )}
            {profile.account_privacy === "private" && (
              <Lock size={13} className="text-muted-foreground ml-0.5" />
            )}
          </div>
          <div
            className="text-muted-foreground text-[14px] mt-0.5"
            data-testid="profile-username"
          >
            @{profile.username}
          </div>
          {bio && (
            <p
              className="text-foreground text-[15px] mt-2.5 leading-[21px]"
              data-testid="profile-bio"
            >
              {bio}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5">
            {location && (
              <span
                className="inline-flex items-center gap-1 text-muted-foreground text-[13.5px]"
                data-testid="profile-location"
              >
                <MapPin size={14} />
                {location}
              </span>
            )}
            {website && (
              <a
                href={website.startsWith("http") ? website : `https://${website}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[13.5px] font-semibold hover:opacity-80"
                style={{ color: BRAND }}
                data-testid="profile-website"
              >
                <LinkIcon size={14} />
                {website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </a>
            )}
          </div>

          {/* Actions row — Edit profile (full-width secondary) + tiny red
              log-out box on the right, matching iOS spec 14 exactly. */}
          <div className="flex gap-2.5 mt-4">
            <button
              type="button"
              onClick={() => navigate("/settings/edit-profile")}
              className="flex-1 h-[42px] rounded-xl border border-border bg-secondary flex items-center justify-center gap-2 hover:opacity-90 active:opacity-80 transition-opacity"
              data-testid="profile-edit"
            >
              <Edit3 size={16} className="text-foreground" strokeWidth={1.8} />
              <span className="text-foreground font-bold text-[14px]">
                Edit profile
              </span>
            </button>
            <button
              type="button"
              onClick={() => setLogoutOpen(true)}
              className="w-[46px] h-[42px] rounded-xl border border-border flex items-center justify-center hover:bg-secondary transition-colors"
              aria-label="Log out"
              data-testid="profile-logout"
            >
              <LogOut size={16} className="text-[#FF3B30]" strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {/* Content tabs */}
        <div className="mt-6 border-t border-border">
          <div className="flex">
            <button
              type="button"
              onClick={() => setTab("posts")}
              className="flex-1 h-11 flex items-center justify-center relative"
              data-testid="profile-tab-posts"
            >
              <span
                className={`text-[13px] font-extrabold uppercase tracking-wide ${
                  tab === "posts" ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                Posts
              </span>
              {tab === "posts" && (
                <span
                  className="absolute bottom-0 left-1/2 -translate-x-1/2"
                  style={{ width: 28, height: 3, borderRadius: 1.5, background: RAINBOW }}
                />
              )}
            </button>
            <button
              type="button"
              onClick={() => setTab("opinion")}
              className="flex-1 h-11 flex items-center justify-center relative"
              data-testid="profile-tab-opinion"
            >
              <span
                className={`text-[13px] font-extrabold uppercase tracking-wide flex items-center gap-1.5 ${
                  tab === "opinion" ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                <BookOpen size={13} strokeWidth={2} />
                Opinion
              </span>
              {tab === "opinion" && (
                <span
                  className="absolute bottom-0 left-1/2 -translate-x-1/2"
                  style={{ width: 36, height: 3, borderRadius: 1.5, background: RAINBOW }}
                />
              )}
            </button>
          </div>

          {/* Tab body */}
          {tab === "posts" ? (
            postsLoading ? (
              <div className="py-14 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: BRAND }} />
              </div>
            ) : posts.length === 0 ? (
              <div className="py-14 flex flex-col items-center gap-2 text-center" data-testid="profile-posts-empty">
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                  <Edit3 size={20} className="text-muted-foreground" />
                </div>
                <h3 className="text-foreground text-[15px] font-bold mt-1">
                  Share your first post.
                </h3>
                <p className="text-muted-foreground text-[13px] max-w-[240px]">
                  Anything you post shows up here.
                </p>
              </div>
            ) : (
              <div
                className="flex flex-wrap px-3 gap-[3px]"
                data-testid="profile-posts-grid"
              >
                {posts.map((p) => (
                  <PostThumbCell
                    key={String(p.id ?? p.post_id)}
                    post={p}
                    onClick={() => navigate(`/post/${p.id ?? p.post_id}`)}
                    testId={`profile-post-thumb-${p.id ?? p.post_id}`}
                  />
                ))}
              </div>
            )
          ) : opinionLoading ? (
            <div className="py-14 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: BRAND }} />
            </div>
          ) : !opinionPosts || opinionPosts.length === 0 ? (
            <div className="py-14 flex flex-col items-center gap-2 text-center" data-testid="profile-opinion-empty">
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                <BookOpen size={20} className="text-muted-foreground" />
              </div>
              <h3 className="text-foreground text-[15px] font-bold mt-1">
                No opinions yet.
              </h3>
              <p className="text-muted-foreground text-[13px] max-w-[240px]">
                Share a view and let the community respond.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border" data-testid="profile-opinion-list">
              {opinionPosts.map((p) => (
                <PostCard key={String(p.id ?? p.post_id)} post={p} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Logout confirm dialog */}
      {logoutOpen && (
        <LogoutDialog
          onCancel={() => setLogoutOpen(false)}
          onConfirm={doLogout}
          loggingOut={loggingOut}
        />
      )}

      {/* Placeholder to keep imports X used consistently */}
      <X className="hidden" />
    </div>
  );
}
